import {
  APICallState,
  ButtonType,
  PanelContainer,
  Selectors,
  Toast,
  ToastMessageContentItemType,
  ToastType,
  useIsDesktop,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useDispatch, useSelector} from 'Provider';
import {Box, Text, View} from 'native-base';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import GenericAPIErrorDisplay from 'components/Offers/GenericAPIErrorDisplay';
import ReCaptcha from 'components/ReCaptcha';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import {
  FREEDOM_PAY_IFRAME_ORIGIN,
  FREEDOM_PAY_UAT_IFRAME_ORIGIN,
  FreedomPayMessage,
  FreedomPayMessageType,
} from 'models/FreedomPay';
import {Platform, StyleSheet} from 'react-native';
import {scrollToContentTop} from 'screens/Profile';
import {isStaging} from 'services/api/api';
import {getIframe} from 'services/api/freedompay';
import {CardIssuerCodeLookup} from 'services/api/paymentintegration';
import {PaymentsActions} from 'store/payments/payments.reducer';
import {
  getSessionKeyInvalid,
  saveCardCallStateSelector,
} from 'store/payments/payments.selectors';
import {PAYMENT_MODULE_STATE, ProfileActions} from 'store/profile';
import {renderFormField} from './renderFormField';

// Field limits:
const CardHolderNameLimit = 26;
const NicknameLimit = 20;

const mainStyles = StyleSheet.create({
  statusDisplay: {
    position: 'absolute',
    width: '100%',
    height: 360,
    zIndex: 999,
  },
  iframeContainerDivRef: {width: '100%', height: 'auto'},
});
interface CustomMessage {
  origin: string;
  data: FreedomPayMessage;
}

const ConnectedFreedomPayInfoModule: React.FC = () => {
  const sessionKeyInvalid = useSelector(getSessionKeyInvalid);
  const dispatch = useDispatch();
  const isDesktop = useIsDesktop();
  const [height, setHeight] = useState(0);
  const [iframeSrc, setIframeSrc] = useState('');
  const iframeContainerDivRef = useRef<HTMLDivElement>(null);
  const {trackAdobeEvent} = useTrackEvent();
  const [iframeLoadStatus, setIframeLoadStatus] = useState(
    APICallState.INITIAL
  );
  const iframeLoaded = iframeLoadStatus === APICallState.SUCCESS;
  const saveCardCallStatus = useSelector(saveCardCallStateSelector);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const name = useWidgetState(Selectors.userFullNameSelector) as string;
  const [cardHolder, setCardHolder] = useState(name);
  const [cardHolderError, setCardHolderError] = useState(false);
  const [cardNickname, setCardNickname] = useState('');
  const iframeURL = isStaging
    ? FREEDOM_PAY_UAT_IFRAME_ORIGIN
    : FREEDOM_PAY_IFRAME_ORIGIN;
  const [messageData, setMessageData] = useState<any>(null);
  const [token, setToken] = useState('');
  const [readyForSubmit, setReadyForSubmit] = useState(false);
  const recaptchaSiteKey = isStaging
    ? '6Le4RccpAAAAACoSn6fO8thrSSL7rJ2VaH8VeoJy'
    : '6LeGbTwrAAAAAA4hjAHU-qzDzaVL2N75_fnne8lR';

  useEffect(() => {
    if (sessionKeyInvalid) {
      setIframeLoadStatus(APICallState.INITIAL);
      dispatch(PaymentsActions.setSessionKeyInvalid(false));
    }
  }, [dispatch, sessionKeyInvalid]);

  const getIframeElement = useCallback(
    () => iframeContainerDivRef.current?.getElementsByTagName('iframe')[0],
    []
  );

  const verify = () => {
    if (window.grecaptcha && window.grecaptcha.execute) {
      window.grecaptcha.execute();
    }
  };

  const onSubmit = useCallback(async () => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {name: 'payment method updates saved'},
    });

    const iframe = getIframeElement();
    const src = iframe?.src;
    let sessionKey = '';
    if (src) {
      // convert the query string to a query params object
      const queryString = src.split('?')[1] || src;

      const searchParams = new URLSearchParams(queryString);

      if (searchParams.get('sessionKey')) {
        sessionKey = searchParams.get('sessionKey') as string;
      }
    }

    const paymentKey = messageData.paymentKeys[1] ?? messageData.paymentKeys[0];

    const cardIssuerFullName =
      messageData?.attributes
        ?.find(a => a.Key === 'CardIssuer')
        ?.Value?.toUpperCase() ?? '';

    const cardIssuer =
      CardIssuerCodeLookup[cardIssuerFullName] ?? CardIssuerCodeLookup.None;
    const cardExpiration = messageData?.attributes?.find(
      a => a.Key === 'ExpirationDate'
    )?.Value;
    const cardMask = messageData?.attributes?.find(
      a => a.Key === 'MaskedCardNumber'
    )?.Value;

    dispatch(
      PaymentsActions.saveCard({
        paymentKey,
        sessionKey,
        cardExpiration,
        cardMask,
        cardIssuer,
        cardNickname,
        cardHolder,
        cardIssuerFullName,
        recaptchaToken: token,
      })
    );
  }, [
    dispatch,
    getIframeElement,
    cardHolder,
    cardNickname,
    trackAdobeEvent,
    messageData,
  ]);

  useEffect(() => {
    async function fetchIframe() {
      try {
        setIframeLoadStatus(APICallState.LOADING);
        const iframeRes = await getIframe();

        const iframe = iframeRes?.data;
        if (iframe) {
          dispatch(PaymentsActions.setSessionKeyInvalid(false));
          const sizedIframe =
            '<meta name="viewport" content="initial-scale=1.0, maximum-scale=1.0">' +
            '<iframe style="width: 100%; height:100%; margin-top:-15px;"' +
            iframe.substring(7) +
            '</meta>';
          setIframeSrc(sizedIframe);
        }
      } catch (e) {
        setIframeLoadStatus(APICallState.ERROR);
      }
    }

    if (iframeLoadStatus === APICallState.INITIAL) {
      fetchIframe();
    }
  }, [iframeLoadStatus, dispatch]);

  const setIframeHeight = useCallback(
    (newHeight: number) => {
      const iframe = getIframeElement();
      if (iframe && newHeight) {
        if (!iframeLoaded) {
          setIframeLoadStatus(APICallState.SUCCESS);
        }
        setHeight(newHeight);
      }
    },
    [getIframeElement, iframeLoaded]
  );
  const handleFreedomPayMessage = useCallback(
    (e: CustomMessage) => {
      // ignore non-FreedomPay messages
      if (
        !(
          e.origin === FREEDOM_PAY_IFRAME_ORIGIN ||
          e.origin === FREEDOM_PAY_UAT_IFRAME_ORIGIN
        )
      ) {
        return;
      }
      const message: FreedomPayMessage = e.data;

      switch (message.type) {
        // Errors
        case FreedomPayMessageType.ERROR:
          setShowErrorToast(true);
          dispatch(PaymentsActions.saveCardFailure());
          break;
        // Height Update
        case FreedomPayMessageType.HEIGHT_UPDATE:
          setIframeHeight(message.data.height);
          break;
        // Submit
        case FreedomPayMessageType.SUBMIT:
          setToken('');
          setReadyForSubmit(false);
          setMessageData(message.data);
          verify();
          break;
        // Validation Updates
        case FreedomPayMessageType.VALIDATION_UPDATE:
          // input validation - nothing to do here, we use inline validation provided by FreedomPay
          if (message.data.isValid === false) {
            dispatch(PaymentsActions.resetSaveCardCallState());
            scrollToContentTop();
          }
          break;
      }
    },
    [
      dispatch,
      verify,
      setMessageData,
      setIframeHeight,
      setShowErrorToast,
      setToken,
      setReadyForSubmit,
    ]
  );

  useEffect(() => {
    if (Platform.OS === 'web' && global.window) {
      global.window.addEventListener('message', handleFreedomPayMessage);
      return () => {
        global.window.removeEventListener('message', handleFreedomPayMessage);
      };
    }
  }, [handleFreedomPayMessage]);

  const handleToken = (recaptchaToken: string) => {
    if (!recaptchaToken) {
      setShowErrorToast(true);
      dispatch(PaymentsActions.saveCardFailure());
      return;
    }
    setToken(recaptchaToken);
    setReadyForSubmit(true);
  };

  useEffect(() => {
    if (readyForSubmit) {
      onSubmit();
    }
  }, [readyForSubmit, token, messageData]);

  return iframeLoadStatus === APICallState.ERROR ? (
    <GenericAPIErrorDisplay
      onRetryPress={() => setIframeLoadStatus(APICallState.INITIAL)}
    />
  ) : (
    <PanelContainer
      isLoading={!iframeLoaded || saveCardCallStatus === APICallState.LOADING}
      onSave={() => {}}
      secondaryCTA={{
        type: ButtonType.SECONDARY,
        label: 'Cancel',
        onPress: () => {
          scrollToContentTop();
          dispatch(PaymentsActions.resetSaveCardCallState());
          dispatch(
            ProfileActions.updatePaymentModuleState(
              PAYMENT_MODULE_STATE.DEFAULT
            )
          );
        },
      }}
      primaryCTA={
        iframeLoaded
          ? {
              label: 'Save',
              onPress: () => {
                if (!cardHolder) {
                  setCardHolderError(true);
                  scrollToContentTop();
                  return;
                }
                setShowErrorToast(false);
                iframeContainerDivRef?.current
                  ?.querySelector('iframe')
                  ?.contentWindow?.postMessage('getPaymentKeys', iframeURL);
              },
            }
          : undefined
      }
      contentMaxWidth="100%"
      panelTitle="ADD A PAYMENT"
      icon="credit-card"
      isSaving={saveCardCallStatus === APICallState.LOADING}>
      <>
        <View maxWidth="678px">
          <Text>
            Great news. The easiest way to make reservations is also the most
            secure. Just add a card to start booking rooms, tables, and more.
          </Text>
        </View>

        <View marginTop={isDesktop ? '48px' : '32px'}>
          {iframeLoaded && (
            <Box marginBottom="16px">
              {renderFormField({
                label: 'Card Nickname',
                value: cardNickname,
                isOptional: true,
                maxLength: NicknameLimit,
                onChangeText: text => {
                  setCardNickname(text.substring(0, NicknameLimit));
                },
              })}
              {renderFormField({
                label: 'Name On Card',
                value: cardHolder,
                isOptional: false,
                onChangeText: text => {
                  if (/^[\s\p{L}0-9_.-]*$/u.test(text)) {
                    setCardHolderError(false);
                    setCardHolder(text.substring(0, CardHolderNameLimit));
                  }
                },
                onBlur: () => {
                  if (!cardHolder) {
                    setCardHolderError(true);
                  }
                },
                isInvalid: cardHolderError,
                errorMessage: 'Name On Card is Required',
                maxLength: CardHolderNameLimit,
              })}
            </Box>
          )}

          {Platform.OS === 'web' && (
            <div
              id={'iframe-container-div'}
              ref={iframeContainerDivRef}
              style={{...mainStyles.iframeContainerDivRef, height: height}}
              dangerouslySetInnerHTML={{__html: iframeSrc}}
            />
          )}
        </View>
      </>
      {showErrorToast && (
        <View w={'100%'} px={Platform.OS === 'web' ? undefined : '4px'}>
          <Toast
            toast={{
              type: ToastType.DANGER,
              messageContent: [
                {
                  elementType: ToastMessageContentItemType.TEXT,
                  text: 'Sorry, we were unable to update your payment information. Please try again.',
                },
              ],
            }}
          />
        </View>
      )}
      <ReCaptcha siteKey={recaptchaSiteKey} callback={handleToken} />
    </PanelContainer>
  );
};

export default ConnectedFreedomPayInfoModule;
